import { ArrowRight, BellRing, Check, HeartHandshake, Leaf, LockKeyhole, Play, Smartphone, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHero } from '../components/PageHero'
import { PageMeta } from '../components/PageMeta'
import { Reveal } from '../components/Reveal'
import { SectionHeading } from '../components/SectionHeading'
import { patientApps } from '../data/site'

export default function AppsPage() {
  return (
    <>
      <PageMeta title="Patient apps" description="Preview two privacy-minded Android health tools in development: a nutrition companion and a practical patient organiser." />
      <PageHero
        eyebrow="Health, in your pocket"
        title={<>Technology that supports the habit—not the <span className="serif-accent">pressure.</span></>}
        copy="Two Android projects are taking shape around a simple idea: health tools should reduce cognitive load, protect privacy, and make the next conversation easier."
        tone="lime"
        aside={<div className="app-hero-badge"><Smartphone size={30} /><strong>Android-first</strong><span>Built for simple daily use</span></div>}
      />

      <section className="section apps-art-section" id="app-toolkit">
        <div className="container apps-art-grid">
          <Reveal className="apps-art-copy">
            <span className="eyebrow">One connected toolkit</span>
            <h2>Food notes, visit prep, and consent you can <span className="serif-accent">actually see.</span></h2>
            <p>These concepts are being shaped around ordinary moments: remembering what changed, spotting a routine, collecting questions, and deciding exactly what to share with a clinician.</p>
            <div className="apps-art-points">
              <span><LockKeyhole size={18} /><strong>Private by design</strong><small>Collect less; explain every permission.</small></span>
              <span><BellRing size={18} /><strong>Gentle prompts</strong><small>No diagnosis, pressure, or punishment.</small></span>
              <span><HeartHandshake size={18} /><strong>Made for dialogue</strong><small>Bring a clearer story into the appointment.</small></span>
            </div>
          </Reveal>
          <Reveal className="apps-art-figure" delay={0.1}>
            <img src="/images/patient-apps.webp" alt="Colorful 3D phones with nutrition, hydration, activity, and appointment-planning motifs" />
            <p>Concept illustration · final product screens and features may change during testing.</p>
          </Reveal>
        </div>
      </section>

      <section className="section app-showcase-section">
        <div className="container app-showcase-list">
          <Reveal className="app-showcase app-showcase-lime">
            <div className="app-showcase-copy">
              <div className="app-name-row"><span className="app-icon"><Leaf size={26} /></span><span><small>{patientApps[0].status}</small><strong>{patientApps[0].name}</strong></span></div>
              <span className="eyebrow">{patientApps[0].label}</span>
              <h2>Notice the pattern. Keep the person.</h2>
              <p>{patientApps[0].description}</p>
              <ul className="feature-checks">{patientApps[0].features.map((item) => <li key={item}><Check size={17} />{item}</li>)}</ul>
              <button className="button button-disabled" type="button" disabled><Play size={17} /> Play Store link coming soon</button>
            </div>
            <div className="app-phone-art">
              <div className="phone phone-app-detail">
                <div className="phone-screen nutrition-detail-screen">
                  <div className="screen-head"><span>Good morning</span><b>12 Oct</b></div>
                  <h3>One choice at a time.</h3>
                  <div className="nutrition-wheel"><div><strong>72%</strong><span>daily rhythm</span></div></div>
                  <div className="habit-tile"><span className="habit-icon">💧</span><div><strong>Water</strong><small>5 of 7 gentle check-ins</small></div><span>+1</span></div>
                  <div className="habit-tile"><span className="habit-icon">🥗</span><div><strong>Balanced plate</strong><small>2 meals noticed</small></div><span>+1</span></div>
                  <p className="screen-affirmation">Consistency can be quiet.</p>
                </div>
              </div>
              <span className="floating-app-chip chip-one"><Sparkles size={15} /> No calorie shame</span>
              <span className="floating-app-chip chip-two"><LockKeyhole size={15} /> Private by design</span>
            </div>
          </Reveal>

          <Reveal className="app-showcase app-showcase-aqua">
            <div className="app-showcase-copy">
              <div className="app-name-row"><span className="app-icon"><HeartHandshake size={26} /></span><span><small>{patientApps[1].status}</small><strong>{patientApps[1].name}</strong></span></div>
              <span className="eyebrow">{patientApps[1].label}</span>
              <h2>Arrive prepared. Leave with the plan.</h2>
              <p>{patientApps[1].description}</p>
              <ul className="feature-checks">{patientApps[1].features.map((item) => <li key={item}><Check size={17} />{item}</li>)}</ul>
              <button className="button button-disabled" type="button" disabled><Play size={17} /> Play Store link coming soon</button>
            </div>
            <div className="app-phone-art">
              <div className="phone phone-app-detail">
                <div className="phone-screen helper-detail-screen">
                  <div className="screen-head"><span>Visit brief</span><b>3 items</b></div>
                  <h3>Ready for Tuesday</h3>
                  <div className="visit-step is-done"><span><Check size={15} /></span><div><strong>Symptoms timeline</strong><small>Updated today</small></div></div>
                  <div className="visit-step"><span>2</span><div><strong>Medicines list</strong><small>Check doses</small></div></div>
                  <div className="visit-step"><span>3</span><div><strong>Your questions</strong><small>Two saved</small></div></div>
                  <button type="button">Preview my brief <ArrowRight size={15} /></button>
                </div>
              </div>
              <span className="floating-app-chip chip-one"><BellRing size={15} /> Calm reminders</span>
              <span className="floating-app-chip chip-two"><LockKeyhole size={15} /> You control sharing</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section app-principles-section">
        <div className="container">
          <Reveal><SectionHeading align="center" eyebrow="Product principles" title={<>Useful by default. <span className="serif-accent">Responsible by design.</span></>} /></Reveal>
          <div className="app-principle-grid">
            {[
              { icon: LockKeyhole, title: 'Privacy before analytics', copy: 'Minimise collection, keep controls understandable, and never sell health-related data.' },
              { icon: BellRing, title: 'Prompts, not pressure', copy: 'Support gentle routines without alarm fatigue, guilt, or streaks that punish real life.' },
              { icon: HeartHandshake, title: 'A bridge to care', copy: 'Help organise questions and patterns without pretending software can diagnose a person.' },
            ].map((item) => <Reveal className="app-principle" key={item.title}><item.icon size={24} /><h3>{item.title}</h3><p>{item.copy}</p></Reveal>)}
          </div>
        </div>
      </section>

      <section className="section app-feedback-section"><div className="container app-feedback-panel"><div><span className="eyebrow">Help shape what comes next</span><h2>What would make a health app genuinely useful to you?</h2><p>Share a focused idea or friction point through the private community research intake.</p></div><Link className="button button-light" to="/community">Contribute an idea <ArrowRight size={17} /></Link></div></section>
    </>
  )
}
